/**
 * Career Domain Explorer API Client
 */

import type {
  CareerDomain,
  CareerSpecialization,
  UserProfile,
  DomainMatch,
  ProfileInput,
  MatchInput
} from '../types';

const API_BASE = '/api/explore/career';

/**
 * Get all career domains
 */
export async function getDomains(): Promise<CareerDomain[]> {
  const response = await fetch(`${API_BASE}/domains`);

  if (!response.ok) {
    throw new Error('Failed to fetch domains');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get domain by slug with specializations
 */
export async function getDomainBySlug(slug: string): Promise<CareerDomain> {
  const response = await fetch(`${API_BASE}/domains/${slug}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Domain not found');
    }
    throw new Error('Failed to fetch domain');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get specialization by slug
 */
export async function getSpecializationBySlug(slug: string): Promise<CareerSpecialization> {
  const response = await fetch(`${API_BASE}/specializations/${slug}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Specialization not found');
    }
    throw new Error('Failed to fetch specialization');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Save user profile
 */
export async function saveProfile(profile: ProfileInput): Promise<UserProfile> {
  const response = await fetch(`${API_BASE}/profile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(profile)
  });

  if (!response.ok) {
    throw new Error('Failed to save profile');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get user profile by session ID
 */
export async function getProfile(sessionId: string): Promise<UserProfile | null> {
  const response = await fetch(`${API_BASE}/profile/${sessionId}`);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error('Failed to fetch profile');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Calculate domain matches based on user profile
 */
export async function calculateMatches(profile: MatchInput): Promise<DomainMatch[]> {
  const response = await fetch(`${API_BASE}/match`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(profile)
  });

  if (!response.ok) {
    throw new Error('Failed to calculate matches');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get or create session ID for anonymous users
 */
export function getSessionId(): string {
  let sessionId = localStorage.getItem('career_session_id');

  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('career_session_id', sessionId);
  }

  return sessionId;
}
